import { BaseError, formatGwei, } from 'viem';
import {} from 'viem/accounts';
import { estimateGas, getChainId as getChainId_, getTransactionCount, } from 'viem/actions';
import { assertRequest, getAction, parseAccount, } from 'viem/utils';
import { estimateFee, } from 'viem/zksync';
import { CONTRACT_DEPLOYER_ADDRESS } from '../constants.js';
export const defaultParameters = [
    'blobVersionedHashes',
    'chainId',
    'fees',
    'gas',
    'nonce',
    'type',
];
export class MaxFeePerGasTooLowError extends BaseError {
    constructor({ maxPriorityFeePerGas }) {
        super(`\`maxFeePerGas\` cannot be less than the \`maxPriorityFeePerGas\` (${formatGwei(maxPriorityFeePerGas)} gwei).`, { name: 'MaxFeePerGasTooLowError' });
    }
}
/**
 * Prepares a transaction request for signing.
 *
 * - Docs: https://viem.sh/docs/actions/wallet/prepareTransactionRequest
 *
 * @param args - {@link PrepareTransactionRequestParameters}
 * @returns The transaction request. {@link PrepareTransactionRequestReturnType}
 *
 * @example
 * import { createWalletClient, custom } from 'viem'
 * import { mainnet } from 'viem/chains'
 * import { prepareTransactionRequest } from 'viem/actions'
 *
 * const client = createWalletClient({
 *   chain: mainnet,
 *   transport: custom(window.ethereum),
 * })
 * const request = await prepareTransactionRequest(client, {
 *   account: '0xA0Cf798816D4b9b9866b5330EEa46a18382f251e',
 *   to: '0x0000000000000000000000000000000000000000',
 *   value: 1n,
 * })
 *
 * @example
 * // Account Hoisting
 * import { createWalletClient, http } from 'viem'
 * import { privateKeyToAccount } from 'viem/accounts'
 * import { mainnet } from 'viem/chains'
 * import { prepareTransactionRequest } from 'viem/actions'
 *
 * const client = createWalletClient({
 *   account: privateKeyToAccount('0x…'),
 *   chain: mainnet,
 *   transport: custom(window.ethereum),
 * })
 * const request = await prepareTransactionRequest(client, {
 *   to: '0x0000000000000000000000000000000000000000',
 *   value: 1n,
 * })
 */
export async function prepareTransactionRequest(client, signerClient, publicClient, args) {
    const { isInitialTransaction, chain, gas, nonce, parameters = defaultParameters, } = args;
    const initiatorAccount = parseAccount((isInitialTransaction ?? false) ? signerClient.account : client.account);
    const request = {
        ...args,
        from: initiatorAccount.address,
    };
    let chainId;
    async function getChainId() {
        if (chainId)
            return chainId;
        if (chain)
            return chain.id;
        if (typeof args.chainId !== 'undefined')
            return args.chainId;
        const chainId_ = await getAction(client, getChainId_, 'getChainId')({});
        chainId = chainId_;
        return chainId;
    }
    if (parameters.includes('chainId'))
        request.chainId = await getChainId();
    if (parameters.includes('nonce') &&
        typeof nonce === 'undefined' &&
        initiatorAccount) {
        request.nonce = await getAction(publicClient, // The public client is more reliable for fetching the latest nonce
        getTransactionCount, 'getTransactionCount')({
            address: initiatorAccount.address,
            blockTag: 'pending',
        });
    }
    let gasFromFeeEstimation;
    if (parameters.includes('fees')) {
        if (typeof request.maxFeePerGas === 'undefined' ||
            typeof request.maxPriorityFeePerGas === 'undefined') {
            let maxFeePerGas;
            let maxPriorityFeePerGas;
            // Skip fee estimation for contract deployments
            if (request.to === CONTRACT_DEPLOYER_ADDRESS) {
                maxFeePerGas = 25000000n;
                maxPriorityFeePerGas = 0n;
            }
            else {
                const estimateFeeRequest = {
                    account: initiatorAccount,
                    to: request.to,
                    value: request.value,
                    data: request.data,
                    gas: request.gas,
                    nonce: request.nonce,
                    chainId: request.chainId,
                    authorizationList: [],
                };
                const feeEstimation = await estimateFee(publicClient, estimateFeeRequest);
                maxFeePerGas = feeEstimation.maxFeePerGas;
                maxPriorityFeePerGas = feeEstimation.maxPriorityFeePerGas;
                gasFromFeeEstimation = feeEstimation.gasLimit;
            }
            if (typeof args.maxPriorityFeePerGas === 'undefined' &&
                args.maxFeePerGas &&
                args.maxFeePerGas < maxPriorityFeePerGas)
                throw new MaxFeePerGasTooLowError({
                    maxPriorityFeePerGas,
                });
            request.maxPriorityFeePerGas = maxPriorityFeePerGas;
            request.maxFeePerGas = maxFeePerGas;
            request.gas = gasFromFeeEstimation;
        }
    }
    if (parameters.includes('gas') &&
        typeof gas === 'undefined' &&
        gasFromFeeEstimation === undefined // if gas was set by fee estimation, don't estimate again
    )
        request.gas = await getAction(client, estimateGas, 'estimateGas')({
            ...request,
            account: initiatorAccount
                ? { address: initiatorAccount.address, type: 'json-rpc' }
                : undefined,
        });
    assertRequest(request);
    delete request.parameters;
    delete request.isInitialTransaction;
    return request;
}
//# sourceMappingURL=prepareTransaction.js.map